import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAdminSettings, updateSiteSettings } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const AdminSettings = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    header_ad_code: '',
    sidebar_ad_code: '',
    footer_ad_code: '',
    popup_ad_code: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }

    fetchSettings();
  }, [isAuthenticated, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await getAdminSettings();
      setSettings({
        header_ad_code: response.data.header_ad_code || '',
        sidebar_ad_code: response.data.sidebar_ad_code || '',
        footer_ad_code: response.data.footer_ad_code || '',
        popup_ad_code: response.data.popup_ad_code || '',
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await updateSiteSettings(settings);
      toast.success('Settings saved!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white">Global Ad Settings</h2>
          <p className="text-gray-500 mt-1">
            Ad codes appear across all pages
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
            {/* Header Ad Code */}
            <div className="bg-[#111] border border-[#222] p-6">
              <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                Header Ad Code
              </label>
              <textarea
                value={settings.header_ad_code}
                onChange={(e) => setSettings({ ...settings, header_ad_code: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-[#222] text-white font-mono text-sm focus:outline-none focus:border-yellow-500 resize-none"
                placeholder="Paste header ad code..."
                rows={5}
                data-testid="header-ad-code-input"
              />
            </div>

            {/* Sidebar Ad Code */}
            <div className="bg-[#111] border border-[#222] p-6">
              <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                Sidebar Ad Code
              </label>
              <textarea
                value={settings.sidebar_ad_code}
                onChange={(e) => setSettings({ ...settings, sidebar_ad_code: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-[#222] text-white font-mono text-sm focus:outline-none focus:border-yellow-500 resize-none"
                placeholder="Paste sidebar ad code..."
                rows={5}
                data-testid="sidebar-ad-code-input"
              />
            </div>

            {/* Footer Ad Code */}
            <div className="bg-[#111] border border-[#222] p-6">
              <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                Footer Ad Code
              </label>
              <textarea
                value={settings.footer_ad_code}
                onChange={(e) => setSettings({ ...settings, footer_ad_code: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-[#222] text-white font-mono text-sm focus:outline-none focus:border-yellow-500 resize-none"
                placeholder="Paste footer ad code..."
                rows={5}
                data-testid="footer-ad-code-input"
              />
            </div>

            {/* Popup Ad Code */}
            <div className="bg-[#111] border border-[#222] p-6">
              <label className="text-xs font-bold uppercase tracking-wider text-yellow-500 block mb-3">
                Popup Ad Code
              </label>
              <textarea
                value={settings.popup_ad_code}
                onChange={(e) => setSettings({ ...settings, popup_ad_code: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-[#222] text-white font-mono text-sm focus:outline-none focus:border-yellow-500 resize-none"
                placeholder="Paste popup ad code..."
                rows={5}
                data-testid="popup-ad-code-input"
              />
            </div>

            {/* Save Button */}
            <button
              type="submit"
              disabled={saving}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 w-full"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              {saving ? 'Saving...' : 'SAVE AD SETTINGS'}
            </button>
          </form>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
